import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { StatusBadge } from '@pazportal/ui';
import { useAllLocations } from '../hooks/useAllLocations';
import { useStaffUsers } from '../hooks/useStaffUsers';
import { useCreateRun, useTripRuns, type PlannedStop, type TripRun } from '../hooks/useTripContinuity';
import { inputClass } from '../components/trips/TripControls';
import { formatDate } from '../lib/formatters';

const RUN_STATE_LABEL: Record<TripRun['run_state'], string> = {
  empty: 'No stops',
  planned: 'Planned',
  underway: 'Underway',
  done: 'Done',
};

const btn = 'rounded px-3 py-1.5 min-h-[40px] text-xs font-medium disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

/**
 * A run is one outing across several properties. Each stop is an ordinary
 * trip (its own reports, open items and billing), so nothing about a property
 * is lost because it was visited as part of a run.
 */
export function TripRunsPage() {
  const { data, isLoading, isError } = useTripRuns();
  const [params, setParams] = useSearchParams();
  const planning = params.get('plan') === '1';
  const focusRun = params.get('run');
  const runs = data ?? [];

  const setPlanning = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set('plan', '1');
    else next.delete('plan');
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Trip Runs</h1>
          <p className="text-sm text-text-muted mt-0.5">One outing, several properties. Each stop keeps its own reports, open items and billing.</p>
        </div>
        {!planning && (
          <button type="button" onClick={() => setPlanning(true)} className={`${btn} bg-primary hover:bg-primary-hover text-text-inverse`}>
            Plan a run
          </button>
        )}
      </div>

      {planning && <PlanRunForm onDone={() => setPlanning(false)} />}

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted" role="status">Loading runs…</div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-notification">Could not load runs. Refresh to try again.</div>
      ) : runs.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
          No runs yet. Plan one to schedule several properties in a single outing.
        </div>
      ) : (
        <ul className="space-y-3">
          {runs.map((r) => (
            <RunCard key={r.run_id} run={r} highlighted={r.run_id === focusRun} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RunCard({ run, highlighted }: { run: TripRun; highlighted: boolean }) {
  const openItems = run.stops.reduce((n, s) => n + (s.open_items ?? 0), 0);
  return (
    <li className={`rounded-lg border bg-surface p-4 space-y-3 ${highlighted ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-text-muted">{run.run_number}</span>
            <span className="rounded-full bg-surface-3 px-2 py-0.5 text-xs font-medium text-text">{RUN_STATE_LABEL[run.run_state]}</span>
          </div>
          <h2 className="text-base font-semibold text-text mt-0.5">{run.title}</h2>
          <p className="text-xs text-text-muted">
            {run.start_date ? formatDate(run.start_date) : 'No dates'}
            {run.end_date && run.end_date !== run.start_date ? ` → ${formatDate(run.end_date)}` : ''}
            {run.lead_technician_name ? ` · ${run.lead_technician_name}` : ''}
          </p>
        </div>
        <p className="text-xs text-text-muted shrink-0">
          {run.completed_count} of {run.stop_count} stops done
          {openItems > 0 && <span className="text-warning"> · {openItems} open item{openItems === 1 ? '' : 's'} across stops</span>}
        </p>
      </div>
      {run.notes && <p className="text-sm text-text-muted whitespace-pre-wrap">{run.notes}</p>}
      <ol className="space-y-1.5">
        {run.stops.map((s) => (
          <li key={s.trip_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-divider px-3 py-2 text-sm">
            <span className="w-6 text-text-faint font-mono text-xs">{s.stop ?? '–'}.</span>
            <Link to={`/locations/${s.location_id}`} className="font-medium text-text hover:underline min-w-0 truncate">
              {s.location_name ?? 'Unknown property'}
            </Link>
            <span className="text-xs text-text-muted">
              {s.start_date ? formatDate(s.start_date) : ''}
              {s.end_date && s.end_date !== s.start_date ? ` → ${formatDate(s.end_date)}` : ''}
            </span>
            {s.open_items > 0 && <span className="text-xs text-warning">{s.open_items} open</span>}
            <span className="ml-auto flex items-center gap-2">
              <StatusBadge status={s.status} />
              <Link to={`/trips?trip=${s.trip_id}`} className="font-mono text-xs text-primary hover:underline">{s.project_number}</Link>
            </span>
          </li>
        ))}
      </ol>
    </li>
  );
}

const emptyStop = (): PlannedStop => ({ location_id: '', start_date: '', end_date: '', scope: '' });

function PlanRunForm({ onDone }: { onDone: () => void }) {
  const { data: locations } = useAllLocations();
  const { data: staff } = useStaffUsers();
  const create = useCreateRun();
  const [title, setTitle] = useState('');
  const [lead, setLead] = useState('');
  const [notes, setNotes] = useState('');
  const [stops, setStops] = useState<PlannedStop[]>([emptyStop(), emptyStop()]);

  const sortedLocations = useMemo(
    () => [...(locations ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [locations],
  );

  const problems = stops.map((s, i) => {
    if (!s.location_id) return `Stop ${i + 1}: choose a property.`;
    if (!s.start_date) return `Stop ${i + 1}: enter a start date.`;
    if (s.end_date && s.end_date < s.start_date) return `Stop ${i + 1}: ends before it starts.`;
    const prev = stops[i - 1];
    if (prev?.start_date && s.start_date < prev.start_date) return `Stop ${i + 1} starts before stop ${i}; put stops in visiting order.`;
    return null;
  });
  const firstProblem = !title.trim() ? 'Give the run a name.' : problems.find(Boolean) ?? null;

  const update = (i: number, patch: Partial<PlannedStop>) =>
    setStops((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, dir: -1 | 1) =>
    setStops((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  return (
    <form
      className="space-y-4 rounded-lg border border-border bg-surface p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (firstProblem) return;
        create.mutate(
          {
            title: title.trim(),
            leadTechnicianId: lead || null,
            notes: notes.trim(),
            stops: stops.map((s) => ({ ...s, end_date: s.end_date || s.start_date, scope: s.scope.trim() })),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h2 className="text-base font-semibold text-text">Plan a run</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block text-xs text-text-muted space-y-1">
          <span>Run name</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. East Coast — HCC, HDC, HWD — Oct" className={inputClass} />
        </label>
        <label className="block text-xs text-text-muted space-y-1">
          <span>Lead technician</span>
          <select value={lead} onChange={(e) => setLead(e.target.value)} className={inputClass}>
            <option value="">Unassigned</option>
            {(staff ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.full_name ?? u.email}</option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-xs text-text-muted mb-1">Stops, in visiting order</legend>
        {stops.map((s, i) => (
          <div key={i} className="rounded border border-divider p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-text">Stop {i + 1}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move stop ${i + 1} up`} className={`${btn} bg-surface-3 text-text`}>↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === stops.length - 1} aria-label={`Move stop ${i + 1} down`} className={`${btn} bg-surface-3 text-text`}>↓</button>
                <button type="button" onClick={() => setStops((p) => p.filter((_, j) => j !== i))} disabled={stops.length === 1} aria-label={`Remove stop ${i + 1}`} className={`${btn} bg-surface-3 text-notification`}>Remove</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-[2fr_1fr_1fr] gap-2">
              <select value={s.location_id} onChange={(e) => update(i, { location_id: e.target.value })} aria-label={`Stop ${i + 1} property`} className={inputClass}>
                <option value="">Choose a property…</option>
                {sortedLocations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              <input type="date" value={s.start_date} onChange={(e) => update(i, { start_date: e.target.value })} aria-label={`Stop ${i + 1} start`} className={inputClass} />
              <input type="date" value={s.end_date} min={s.start_date || undefined} onChange={(e) => update(i, { end_date: e.target.value })} aria-label={`Stop ${i + 1} end (blank = one day)`} className={inputClass} />
            </div>
            <input value={s.scope} onChange={(e) => update(i, { scope: e.target.value })} placeholder="Scope for this stop (the customer sees this)" aria-label={`Stop ${i + 1} scope`} className={inputClass} />
          </div>
        ))}
        <button type="button" onClick={() => setStops((p) => [...p, emptyStop()])} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>
          Add stop
        </button>
      </fieldset>

      <label className="block text-xs text-text-muted space-y-1">
        <span>Run notes (staff only)</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass} placeholder="Flights, hotel, parts to bring…" />
      </label>

      <p className="text-xs text-text-faint">Each property is notified of its own visit when the run is saved.</p>
      {(firstProblem || create.error) && (
        <p role="alert" className="text-xs text-notification">{create.error ? (create.error as Error).message : firstProblem}</p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>Cancel</button>
        <button type="submit" disabled={Boolean(firstProblem) || create.isPending} className={`${btn} bg-primary hover:bg-primary-hover text-text-inverse`}>
          {create.isPending ? 'Scheduling…' : `Schedule ${stops.length} stop${stops.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </form>
  );
}
